import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast, Toaster } from "sonner";
import {
  Eye,
  ArrowLeft,
  Warehouse,
  MagnifyingGlass,
} from "@phosphor-icons/react";

export function WHAlleyDashboard() {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api
      .get("/sessions")
      .then(({ data }) => setSessions(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <AppShell>
      <Toaster position="top-right" richColors />

      <div className="max-w-4xl mx-auto p-6 space-y-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
            WH-Alley
          </div>

          <h1 className="font-display text-3xl font-bold text-zinc-900 tracking-tight">
            Counter Count View
          </h1>

          <p className="text-sm text-zinc-600">
            View-only. Shows the total counter count and whether the item is
            matched, short, over, or not yet counted.
          </p>
        </div>

        <div className="grid gap-3">
          {sessions.map((s) => (
            <Link
              key={s.id}
              to={`/wh-alley/sessions/${s.id}`}
              className="rounded-sm border border-zinc-300 bg-white p-4 flex items-center justify-between hover:border-indigo-400"
            >
              <div>
                <div className="font-display font-semibold text-zinc-900">
                  {s.session_name}
                </div>

                <div className="text-xs text-zinc-500 mono">
                  {s.warehouse_name || "—"} ·{" "}
                  <span className="uppercase">{s.session_status}</span>
                </div>
              </div>

              <div className="text-sm text-indigo-900 inline-flex items-center gap-1">
                Open <Eye className="w-4 h-4" />
              </div>
            </Link>
          ))}

          {sessions.length === 0 && (
            <div className="rounded-sm border border-zinc-300 bg-white p-10 text-center text-zinc-500">
              No sessions assigned.
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

export function WHAlleySession() {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [supervisor, setSupervisor] = useState("");

  useEffect(() => {
    api
      .get(`/wh-alley/sessions/${id}/items`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  const allItems = useMemo(
    () => (Array.isArray(data?.items) ? data.items : []),
    [data]
  );

  const search = q.trim().toLowerCase();

  const supervisorOptions = useMemo(() => {
    const names = new Set();
    let unassigned = false;
    allItems.forEach((it) => {
      const s = (it.alley_supervisor || "").trim();
      if (s) names.add(s);
      else unassigned = true;
    });
    return {
      names: [...names].sort((a, b) => a.localeCompare(b)),
      unassigned,
    };
  }, [allItems]);

  const items = useMemo(() => {
    return allItems.filter((it) => {
      const sup = (it.alley_supervisor || "").trim();
      if (supervisor === "__unassigned__") {
        if (sup) return false;
      } else if (supervisor && sup !== supervisor) {
        return false;
      }

      if (!search) return true;

      return [
        it.item_description,
        it.tonality,
        it.alley_supervisor,
        it.discrepancy_status,
        it.total_counter_count,
      ].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(search)
      );
    });
  }, [allItems, search, supervisor]);

  const discrepancyClass = (status) => {
    if (status === "Matched") {
      return "text-emerald-700";
    }

    if (status === "Short") {
      return "text-rose-700";
    }

    if (status === "Over") {
      return "text-amber-700";
    }

    if (status === "No Count") {
      return "text-zinc-500";
    }

    return "text-zinc-600";
  };

  if (!data) {
    return (
      <AppShell>
        <Toaster position="top-right" richColors />
        <div className="p-10 text-zinc-500">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Toaster position="top-right" richColors />

      <div className="max-w-5xl mx-auto p-3 sm:p-6 space-y-4">
        <div>
          <Link
            to="/wh-alley"
            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back
          </Link>

          <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-900 tracking-tight mt-1">
            {data.session?.session_name || "Session"}
          </h1>

          <div className="text-sm text-zinc-600 mono">
            <Warehouse className="w-4 h-4 inline mr-1 text-zinc-500" />
            {data.session?.warehouse_name || "—"}
          </div>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white p-3">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass className="w-4 h-4 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2" />

              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search item name, tonality, supervisor, count, or discrepancy..."
                className="w-full rounded-sm border border-zinc-300 pl-8 pr-3 py-2 text-sm"
              />
            </div>

            <select
              value={supervisor}
              onChange={(e) => setSupervisor(e.target.value)}
              className="rounded-sm border border-zinc-300 px-2 py-2 text-sm min-w-[190px]"
              data-testid="wh-supervisor-filter"
            >
              <option value="">All Alley Supervisors</option>
              {supervisorOptions.names.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
              {supervisorOptions.unassigned && (
                <option value="__unassigned__">— Unassigned —</option>
              )}
            </select>
          </div>

          <div className="text-xs text-zinc-500 mono mt-1">
            Showing {items.length} of {allItems.length}
          </div>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white overflow-hidden">
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="data-table text-xs sm:text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-sm">
                <tr>
                  <th>Item Name</th>
                  <th>Tonality</th>
                  <th>Alley Supervisor</th>
                  <th className="text-right">Total Counter Count</th>
                  <th>Discrepancy</th>
                </tr>
              </thead>

              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td className="min-w-[260px]">
                      {it.item_description || "—"}
                    </td>

                    <td className="mono">{it.tonality || "—"}</td>

                    <td className="whitespace-nowrap">
                      {it.alley_supervisor || (
                        <span className="text-zinc-400">—</span>
                      )}
                    </td>

                    <td className="text-right mono font-semibold">
                      {it.total_counter_count ?? 0}
                    </td>

                    <td
                      className={`font-semibold ${discrepancyClass(
                        it.discrepancy_status
                      )}`}
                    >
                      {it.discrepancy_status || "—"}
                    </td>
                  </tr>
                ))}

                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-10 text-zinc-500"
                    >
                      No items to show.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
